//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region Model Imports

import { OpDataType, OpDataFormatGroupDetails, OpDataTypeFormat } from '../../models/core'
import { Enums } from '../../models/common'

//#endregion

//#region Helper Imports

import { TypeHelper, ConvertHelper, LocalizationHelper, ConfigHelper } from '../../helpers'
import { Dictionary, KeyType } from '../../helpers/dictionary'

//#endregion

//#region Service Imports

import { OperatorSettingsService, OperatorProfileDateFormats, OPERATOR_SETTINGS_SERVICE_NAME } from '../operator'
import { SYSTEM_INTERNATIONALIZATION_SERVICE_NAME, SystemInternationalizationService, Formatter } from '../system'
import { UserSessionService, USER_SESSION_SERVICE_NAME } from '../user-session'

//#endregion


//#region Public Types

export const DATA_TYPE_FORMAT_SERVICE_NAME = 'DataTypeFormatService'
export const DATA_TYPE_FORMAT_SERVICE_PROVIDER_NAME = 'DataTypeFormatServiceProvider'


/**
 * Options for the @see {@link DataTypeFormatter.formatDisplayValue} method.
 */
export type FormatDisplayValueOptions = {

    /**
     * A source for idDataType and idDataTypeClass values.
     */
    dataType?: OpDataType

    /**
     * An idDataType for witch data type formats should be used.
     */
    idDataType?: Enums.DataType

    /**
     * An idDataTypeClass of the given value.
     */
    idDataTypeClass?: Enums.DataTypeClass
}

export class DataTypeFormatter {

    //#region Ctor

    public constructor(
        private readonly formatter: Formatter,
        private readonly dataTypeFormatFromFormatGroups: Dictionary<OpDataTypeFormat>,
        private readonly dataTypeFormatFromDataTypes: Dictionary<OpDataTypeFormat>,
        private readonly profileFormats: OperatorProfileDateFormats,
        private readonly configFormats: OperatorProfileDateFormats,
        private readonly defaultFormats: OperatorProfileDateFormats
    ) { }

    //#endregion

    /**
     * Formats the given value according to the given options.
     * 
     * @param value 
     * @param options 
     * 
     * @see {PrepareDisplayValueOptions}
     */
    public formatDisplayValue(value: any, options: FormatDisplayValueOptions): string {
        if (value == null) {
            return TypeHelper.emptyString
        }

        let retVal: string = value.toString()
        let idDataTypeClass: number = null
        let idDataType: number = null

        if (options.dataType != null) {
            idDataTypeClass = options.dataType.idDataTypeClass
            idDataType = options.dataType.idDataType
        }

        if (idDataType == null) {
            idDataType = options.idDataType
        }

        if (idDataTypeClass == null) {
            idDataTypeClass = options.idDataTypeClass
        }

        switch (idDataTypeClass) {
            case Enums.DataTypeClass.Decimal:
            case Enums.DataTypeClass.Real:
            case Enums.DataTypeClass.Integer: {
                // Checking for number because reference type strings have the integer class
                if (_.isNumber(value)) {
                    return this.formatNumber(value, idDataType)
                }

                return value
            }

            case Enums.DataTypeClass.Date: {
                if (_.isString(value)) {
                    const dateTimeFormat: string = this.getFormatForMoment(this.getShortDateFormat(idDataType))
                    value = ConvertHelper.stringToDate(value, dateTimeFormat)
                }

                return this.formatDate(value, idDataType)
            }

            case Enums.DataTypeClass.Time:
            case Enums.DataTypeClass.Datetime: {

                if (_.isString(value)) {
                    const dateTimeFormat: string = this.getFormatForMoment(this.getLongDateFormat(idDataType))
                    value = ConvertHelper.stringToDate(value, dateTimeFormat)
                }

                return this.formatDateTime(value, idDataType)
            }

            case Enums.DataTypeClass.Text:
            case Enums.DataTypeClass.Binary: {
                return value.toString()
            }

            case Enums.DataTypeClass.Boolean: {
                if (value) {
                    return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.True)
                }

                return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.False)
            }
        }

        return retVal
    }

    //#region Number

    private formatNumber(value: number, idDataType: Enums.DataType): string {
        const dataTypeFormat: OpDataTypeFormat = this.getNumberDataTypeFormat(idDataType)
        const globalizeOptions: any = this.getNumberGlobalizeOptions(dataTypeFormat)

        return this.formatter.formatNumber(value, globalizeOptions)
    }

    private getNumberDataTypeFormat(idDataType: number): OpDataTypeFormat {
        let dataTypeFormat: OpDataTypeFormat = this.getDataFormatFromFormatGroups(idDataType)

        if (dataTypeFormat == null) {
            dataTypeFormat = this.getDataFormateFromDataType(idDataType)
        }

        return dataTypeFormat
    }

    private getNumberGlobalizeOptions(dataTypeFormat: OpDataTypeFormat): any {
        const globalizeOptions: any = {}

        if (dataTypeFormat != null) {
            if (dataTypeFormat.numberMaxPrecision != null && dataTypeFormat.numberMaxPrecision > -1) {
                globalizeOptions.maximumFractionDigits = dataTypeFormat.numberMaxPrecision
            }

            if (dataTypeFormat.numberMinPrecision != null) {
                globalizeOptions.minimumFractionDigits = dataTypeFormat.numberMinPrecision
            }

            if (dataTypeFormat.datetimeFormat != null) {
                globalizeOptions.raw = dataTypeFormat.datetimeFormat
            }

            if (
                globalizeOptions.minimumFractionDigits != null
                && globalizeOptions.maximumFractionDigits != null
                && globalizeOptions.minimumFractionDigits > globalizeOptions.maximumFractionDigits
            ) {
                globalizeOptions.minimumFractionDigits = globalizeOptions.maximumFractionDigits
            }
        }

        return globalizeOptions
    }

    //#endregion


    //#region Time/Date/DateTime

    public getDateFormat(formatType: ConfigHelper.DateFormat, idDataType: Enums.DataType): string {
        let format: string = null

        if (formatType === ConfigHelper.DateFormat.DATE_TIME_FORMAT_DX || formatType === ConfigHelper.DateFormat.DATE_TIME_FORMAT_MOMENTJS) {
            format = this.getLongDateFormat(idDataType)
        }

        if (formatType === ConfigHelper.DateFormat.DATE_FORMAT_DX || formatType === ConfigHelper.DateFormat.DATE_FORMAT_MOMENTJS) {
            format = this.getShortDateFormat(idDataType)

        }

        if (formatType === ConfigHelper.DateFormat.DATE_TIME_FORMAT_MOMENTJS || formatType === ConfigHelper.DateFormat.DATE_FORMAT_MOMENTJS) {
            return this.getFormatForMoment(format)
        }

        return format
    }

    private getFormatForMoment(dateFormat: string) {
        let result: string = dateFormat

        if (result != null) {
            result = result.replace(/z/g, 'Z')
            result = result.replace(/y/g, 'Y')
            result = result.replace(/dddd/g, '****')
            result = result.replace(/ddd/g, '***')
            result = result.replace(/d/g, 'D')
            result = result.replace(/\*/g, 'd')
            result = result.replace(/f/g, 'S')
            //result = result.replace(/A/g, 'a')
            result = result.replace(/tt/g, 'A')
        }

        return result
    }

    private formatDate(value: Date, idDataType: number): string {
        const format: string = this.getShortDateFormat(idDataType)

        return this.formatter.formatDate(value, format)
    }

    private getShortDateFormat(idDataType: number): string {
        let dateFormat: string = this.getDateFormatFromDataTypeFormat(idDataType)

        if (dateFormat == null) {
            dateFormat = this.profileFormats.shortFormat
        }

        if (dateFormat == null) {
            dateFormat = this.getDateFormatFromDataType(idDataType)
        }

        if (dateFormat == null) {
            dateFormat = this.configFormats.shortFormat
        }

        if (dateFormat == null) {
            dateFormat = this.defaultFormats.shortFormat
        }        

        return dateFormat
    }

    private formatDateTime(value: Date, idDataType: number): string {
        const format = this.getLongDateFormat(idDataType)

        return this.formatter.formatDate(value, format)
    }

    private getLongDateFormat(idDataType: number): string {
        let dateFormat: string = this.getDateFormatFromDataTypeFormat(idDataType)

        if (dateFormat == null) {
            dateFormat = this.profileFormats.longFormat
        }

        if (dateFormat == null) {
            dateFormat = this.getDateFormatFromDataType(idDataType)
        }

        if (dateFormat == null) {
            dateFormat = this.configFormats.longFormat
        }

        if (dateFormat == null) {
            dateFormat = this.defaultFormats.longFormat
        }

        if (dateFormat != null) {
            dateFormat = dateFormat.replace(/tt/g, 'a') //tt is format for AM/PM on C#
        }

        return dateFormat
    }

    private getDateFormatFromDataTypeFormat(idDataType: number): string {
        const dataTypeFormat: OpDataTypeFormat = this.getDataFormatFromFormatGroups(idDataType)

        if (dataTypeFormat != null) {
            return dataTypeFormat.datetimeFormat
        }

        return null
    }

    private getDateFormatFromDataType(idDataType: number): string {
        const dataTypeFormat: OpDataTypeFormat = this.getDataFormateFromDataType(idDataType)

        if (dataTypeFormat != null) {
            return dataTypeFormat.datetimeFormat
        }

        return null
    }

    //#endregion


    //#region DataTypeFormats

    private getDataFormatFromFormatGroups(idDataType: number): OpDataTypeFormat {
        if (idDataType != null && this.dataTypeFormatFromFormatGroups != null) {
            return this.dataTypeFormatFromFormatGroups.tryGetValue(idDataType)
        }

        return null
    }

    private getDataFormateFromDataType(idDataType: number): OpDataTypeFormat {
        if (idDataType != null && this.dataTypeFormatFromDataTypes != null) {
            return this.dataTypeFormatFromDataTypes.tryGetValue(idDataType)
        }

        return null
    }

    //#endregion
}

//#endregion


export class DataTypeFormatService {

    //#region Consts

    private get LOG_PREFIX(): string { return '[SIMAX.Services.DataType.Format]' }

    //#endregion


    //#region Ctor

    constructor(
        private operatorSettingsService: OperatorSettingsService,
        private systemInternationalizationService: SystemInternationalizationService,
        private userSessionService: UserSessionService
    ) { }

    //#endregion

    /**
     * Returns a formatter object for the given list od data types.
     */
    public getDataTypeFormatter(idDataTypes: Enums.DataType[]): JQueryPromise<DataTypeFormatter> {
        const uniqueIdDataTypes: Enums.DataType[] = _.uniq(idDataTypes)

        return $
            .when(
                this.systemInternationalizationService.getFormatter(),
                this.getDataTypeFormatsFromFormatGroups(uniqueIdDataTypes),
                this.getDataTypeFormatsFromDataTypes(uniqueIdDataTypes),
                this.getDateFormatsFromProfile(),
                this.getDateFormatsFromConfig(),
                this.getDefaultDateFormats()
            )
            .then((
                formatter: Formatter,
                dataTypeFormatFromFormatGroups: Dictionary<OpDataTypeFormat>,
                dataTypeFormatFromDataTypes: Dictionary<OpDataTypeFormat>,
                profileFormats: OperatorProfileDateFormats,
                configFormats: OperatorProfileDateFormats,
                defaultFormats: OperatorProfileDateFormats
            ) => {

                return new DataTypeFormatter(
                    formatter,
                    dataTypeFormatFromFormatGroups,
                    dataTypeFormatFromDataTypes,
                    profileFormats,
                    configFormats,
                    defaultFormats
                )
            })
    }

    private getDataTypeFormatsFromFormatGroups(idDataTypes: Enums.DataType[]): JQueryPromise<Dictionary<OpDataTypeFormat>> {
        return this.userSessionService
            .getDataProvider().Operator
            .getOperatorSettings(this.userSessionService.getIdLoggedOperator(), [Enums.SimaxOperatorSetting.DataTypeFormatGroup])
            .then((result: [OpDataFormatGroupDetails[]]) => {
                const formatDictionary: Dictionary<OpDataTypeFormat> = new Dictionary<OpDataTypeFormat>(null, KeyType.Number)

                _
                    .chain(_.first(result))
                    .filter((dataTypeFormatGroup: OpDataFormatGroupDetails) => {
                        return _.contains(idDataTypes, dataTypeFormatGroup.idDataType) && dataTypeFormatGroup.dataTypeFormatOut != null
                    })
                    .forEach((dataTypeFormatGroup: OpDataFormatGroupDetails) => {
                        formatDictionary.add(dataTypeFormatGroup.idDataType, dataTypeFormatGroup.dataTypeFormatOut)
                    })

                return formatDictionary
            })
    }

    private getDataTypeFormatsFromDataTypes(idDataTypes: Enums.DataType[]): JQueryPromise<Dictionary<OpDataTypeFormat>> {
        return this.userSessionService
            .getDataProvider().DataType
            .getDataTypes(idDataTypes)
            .then((dataTypes: OpDataType[]) => {
                const formatDictionary: Dictionary<OpDataTypeFormat> = new Dictionary<OpDataTypeFormat>(null, KeyType.Number)

                _
                    .chain(dataTypes)
                    .filter((dataType: OpDataType) => {
                        return dataType.dataTypeFormat != null
                    })
                    .forEach((dataType: OpDataType) => {
                        formatDictionary.add(dataType.idDataType, dataType.dataTypeFormat)
                    })

                return formatDictionary
            })
    }

    private getDateFormatsFromProfile(): JQueryPromise<OperatorProfileDateFormats> {
        return this.operatorSettingsService.getDateFormats()
    }

    private getDateFormatsFromConfig() {
        const currentLanguage: string = this.userSessionService.getDataProvider().System.getCurrentLanguage()
        const language: string = currentLanguage.split('-')[0].toUpperCase()
        const dateFormatConfigSection: any = ConfigHelper.getSectionValue(Enums.SimaxConfigSection.Formats)
        const dateFormats = {
            longFormat: null,
            shortFormat: null
        }

        if (dateFormatConfigSection != null) {
            let languageFormats = dateFormatConfigSection[language]

            if (languageFormats == null) {
                languageFormats = ConfigHelper.getDefaultValue(Enums.SimaxConfigSection.Formats)
            }

            if (languageFormats != null) {
                dateFormats.shortFormat = languageFormats['DATE_FORMAT_DX'] || null
                dateFormats.longFormat = languageFormats['DATE_TIME_FORMAT_DX'] || null
            }
        }

        return dateFormats
    }

    private getDefaultDateFormats() {
        return {
            shortFormat: 'yyyy-MM-dd',
            longFormat: 'yyyy-MM-dd HH:mm'
        }
    }
}

export class DataTypeFormatServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        OPERATOR_SETTINGS_SERVICE_NAME,
        SYSTEM_INTERNATIONALIZATION_SERVICE_NAME,
        USER_SESSION_SERVICE_NAME,
        (
            operatorSettingsService: OperatorSettingsService,
            systemInternationalizationService: SystemInternationalizationService,
            userSessionService: UserSessionService
        ) => {
            return new DataTypeFormatService(operatorSettingsService, systemInternationalizationService, userSessionService)
        }]

    //#endregion
}